import { httpClient } from './http-client';

export interface TelemedicineSession {
  id: string;
  appointmentId: string;
  doctorId: string;
  patientId: string;
  roomId: string | null;
  status: 'CREATED' | 'WAITING' | 'READY' | 'CONNECTING' | 'CONNECTED' | 'IN_PROGRESS' | 'PAUSED' | 'RECONNECTING' | 'FINISHED' | 'CANCELLED' | 'FAILED' | 'EXPIRED';
  scheduledAt: string;
  startedAt: string | null;
  endedAt: string | null;
  expiresAt: string;
  duration: number;
  enableChat: boolean;
  enableScreenShare: boolean;
  enableRecording: boolean;
  privacy: string;
  createdAt: string;
  updatedAt: string;
}

export interface TelemedicineParticipant {
  id: string;
  sessionId: string;
  userId: string;
  role: 'DOCTOR' | 'PATIENT' | 'OBSERVER' | 'ADMIN';
  status: 'NOT_JOINED' | 'JOINING' | 'JOINED' | 'CONNECTED' | 'DISCONNECTED' | 'LEFT';
  joinedAt: string | null;
  leftAt: string | null;
}

export interface TelemedicineToken {
  token: string;
  sessionId: string;
  userId: string;
  role: 'DOCTOR' | 'PATIENT' | 'OBSERVER' | 'ADMIN';
  expiresAt: string;
}

export interface RealtimeEvent {
  type: 'PARTICIPANT_JOINED' | 'PARTICIPANT_LEFT' | 'CONNECTION_STATE' | 'MEDIA_STATE' | 'CHAT_MESSAGE' | 'RECORDING_STATE';
  sessionId: string;
  timestamp: string;
  [key: string]: unknown;
}

export const telemedicineApi = {
  async getSession(sessionId: string): Promise<TelemedicineSession | null> {
    return await httpClient.get<TelemedicineSession | null>(`/telemedicine/session/${sessionId}`);
  },

  async getSessionByAppointment(appointmentId: string): Promise<TelemedicineSession | null> {
    return await httpClient.get<TelemedicineSession | null>(`/telemedicine/session/appointment/${appointmentId}`);
  },

  async createSession(payload: {
    appointmentId: string;
    doctorId: string;
    patientId: string;
    scheduledAt?: string;
    duration?: number;
    enableRecording?: boolean;
  }): Promise<TelemedicineSession> {
    return await httpClient.post<TelemedicineSession>('/telemedicine/session', payload);
  },

  async generateToken(payload: {
    sessionId: string;
    userId: string;
    role: 'DOCTOR' | 'PATIENT' | 'OBSERVER';
  }): Promise<TelemedicineToken> {
    return await httpClient.post<TelemedicineToken>(`/telemedicine/session/${payload.sessionId}/token`, {
      userId: payload.userId,
      role: payload.role,
    });
  },

  async joinSession(payload: {
    sessionId: string;
    userId: string;
    role: 'DOCTOR' | 'PATIENT' | 'OBSERVER';
    ipAddress?: string;
  }): Promise<TelemedicineSession> {
    return await httpClient.post<TelemedicineSession>(`/telemedicine/session/${payload.sessionId}/join`, {
      userId: payload.userId,
      role: payload.role,
      ipAddress: payload.ipAddress,
    });
  },

  async leaveSession(payload: { sessionId: string; userId: string }): Promise<TelemedicineSession> {
    return await httpClient.post<TelemedicineSession>(`/telemedicine/session/${payload.sessionId}/leave`, {
      userId: payload.userId,
    });
  },

  async startSession(payload: { sessionId: string; doctorId: string }): Promise<TelemedicineSession> {
    return await httpClient.post<TelemedicineSession>(`/telemedicine/session/${payload.sessionId}/start`, {
      doctorId: payload.doctorId,
    });
  },

  async endSession(payload: {
    sessionId: string;
    endedBy: string;
    reason?: string;
  }): Promise<TelemedicineSession> {
    return await httpClient.post<TelemedicineSession>(`/telemedicine/session/${payload.sessionId}/end`, payload);
  },

  async cancelSession(payload: {
    sessionId: string;
    cancelledBy: string;
    reason: string;
  }): Promise<TelemedicineSession> {
    return await httpClient.post<TelemedicineSession>(`/telemedicine/session/${payload.sessionId}/cancel`, {
      cancelledBy: payload.cancelledBy,
      reason: payload.reason,
    });
  },

  async getSessionParticipants(sessionId: string): Promise<TelemedicineParticipant[]> {
    return await httpClient.get<TelemedicineParticipant[]>(`/telemedicine/session/${sessionId}/participants`);
  },

  async pollRealtimeEvents(sessionId: string, since?: string): Promise<{ events: RealtimeEvent[]; count: number }> {
    const qs = since ? `?since=${encodeURIComponent(since)}` : '';
    return await httpClient.get<{ events: RealtimeEvent[]; count: number }>(
      `/telemedicine/realtime/session/${sessionId}/events${qs}`,
    );
  },

  async emitMediaState(payload: {
    sessionId: string;
    participantId: string;
    camera: boolean;
    microphone: boolean;
    screenShare: boolean;
  }): Promise<{ success: true }> {
    return await httpClient.post<{ success: true }>(`/telemedicine/realtime/session/${payload.sessionId}/media`, {
      participantId: payload.participantId,
      camera: payload.camera,
      microphone: payload.microphone,
      screenShare: payload.screenShare,
    });
  },

  async emitChatMessage(payload: {
    sessionId: string;
    messageId: string;
    participantId: string;
    content: string;
  }): Promise<{ success: true }> {
    return await httpClient.post<{ success: true }>(`/telemedicine/realtime/session/${payload.sessionId}/chat`, payload);
  },
};

export interface RecordingResponse {
  recordingId: string;
  sessionRecordingId?: string;
  url: string | null;
  duration: number | null;
  status: string;
  startedAt?: string;
  endedAt?: string;
}

export interface SessionRecordingResponse {
  id: string;
  sessionId: string;
  recordingId: string | null;
  status: string;
  url: string | null;
  durationSeconds: number | null;
  startedAt: string | null;
  stoppedAt: string | null;
}

export const recordingsApi = {
  async startRecording(sessionId: string): Promise<{ data: RecordingResponse }> {
    return await httpClient.post<{ data: RecordingResponse }>(`/telemedicine/session/${sessionId}/recording/start`);
  },

  async stopRecording(sessionId: string): Promise<{ data: RecordingResponse }> {
    return await httpClient.post<{ data: RecordingResponse }>(`/telemedicine/session/${sessionId}/recording/stop`);
  },

  async getSessionRecordings(sessionId: string): Promise<{ data: SessionRecordingResponse[] }> {
    return await httpClient.get<{ data: SessionRecordingResponse[] }>(`/telemedicine/session/${sessionId}/recordings`);
  },

  async getRecording(recordingId: string): Promise<{ data: RecordingResponse }> {
    return await httpClient.get<{ data: RecordingResponse }>(`/telemedicine/session/recording/${recordingId}`);
  },

  async deleteRecording(recordingId: string): Promise<{ success: boolean }> {
    return await httpClient.delete<{ success: boolean }>(`/telemedicine/session/recording/${recordingId}`);
  },
};